import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

const MetaSdkContext = createContext({
  ready: false,
  loading: false,
  error: null,
  appId: "",
  sdk: null
});

const SDK_SCRIPT_ID = "meta-jssdk";
const SDK_URL =
  "https://connect.facebook.net/pt_BR/sdk.js";

const MetaSdkProvider = ({ children }) => {
  const appId =
    process.env.REACT_APP_META_APP_ID || "";

  const graphVersion =
    process.env.REACT_APP_META_GRAPH_API_VERSION ||
    "v25.0";

  const [ready, setReady] =
    useState(Boolean(window.FB));

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState(null);

  useEffect(() => {
    let active = true;

    if (!appId) {
      setError(
        new Error(
          "REACT_APP_META_APP_ID não configurado."
        )
      );

      return undefined;
    }

    const initializeSdk = () => {
      if (
        !active ||
        !window.FB
      ) {
        return;
      }

      try {
        window.FB.init({
          appId,
          cookie: true,
          xfbml: false,
          version: graphVersion
        });

        setReady(true);
        setLoading(false);
        setError(null);
      } catch (sdkError) {
        setReady(false);
        setLoading(false);
        setError(sdkError);
      }
    };

    if (window.FB) {
      initializeSdk();
      return () => {
        active = false;
      };
    }

    setLoading(true);

    const previousAsyncInit =
      window.fbAsyncInit;

    window.fbAsyncInit = () => {
      if (
        typeof previousAsyncInit ===
        "function"
      ) {
        previousAsyncInit();
      }

      initializeSdk();
    };

    const existingScript =
      document.getElementById(
        SDK_SCRIPT_ID
      );

    if (existingScript) {
      existingScript.addEventListener(
        "load",
        initializeSdk,
        { once: true }
      );

      return () => {
        active = false;
        existingScript.removeEventListener(
          "load",
          initializeSdk
        );
      };
    }

    const script =
      document.createElement("script");

    script.id = SDK_SCRIPT_ID;
    script.src = SDK_URL;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";

    script.onerror = () => {
      if (!active) {
        return;
      }

      setReady(false);
      setLoading(false);
      setError(
        new Error(
          "Não foi possível carregar o SDK da Meta."
        )
      );
    };

    document.body.appendChild(
      script
    );

    return () => {
      active = false;
    };
  }, [appId, graphVersion]);

  const value = useMemo(
    () => ({
      ready,
      loading,
      error,
      appId,
      graphVersion,
      sdk:
        ready && window.FB
          ? window.FB
          : null
    }),
    [
      ready,
      loading,
      error,
      appId,
      graphVersion
    ]
  );

  return (
    <MetaSdkContext.Provider
      value={value}
    >
      {children}
    </MetaSdkContext.Provider>
  );
};

export const useMetaSdk = () =>
  useContext(MetaSdkContext);

export default MetaSdkProvider;
